import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Flow, FlowStep, StepTransition } from "../../../../types";

type TransitionType = "next_step" | "end_flow" | "route_to_flow";

function getType(t: StepTransition): TransitionType {
  if ("next_step_id" in t) return "next_step";
  if ("end_flow" in t) return "end_flow";
  return "route_to_flow";
}

interface Props {
  label: string;
  value: StepTransition;
  onChange: (t: StepTransition) => void;
  steps: FlowStep[];
  flows: Flow[];
}

export const TransitionEditor = ({ label, value, onChange, steps, flows }: Props) => {
  const [type, setType] = useState<TransitionType>(getType(value));
  const [nextStepId, setNextStepId] = useState<string>(
    "next_step_id" in value ? value.next_step_id : ""
  );
  const [endMessage, setEndMessage] = useState<string>(
    "end_flow" in value ? value.message : ""
  );
  const [routeFlowId, setRouteFlowId] = useState<string>(
    "route_to_flow_id" in value ? value.route_to_flow_id : ""
  );

  // Sync when external value changes (e.g. step reselected)
  useEffect(() => {
    const t = getType(value);
    setType(t);
    if ("next_step_id" in value) setNextStepId(value.next_step_id);
    if ("end_flow" in value) setEndMessage(value.message);
    if ("route_to_flow_id" in value) setRouteFlowId(value.route_to_flow_id);
  }, [value]);

  const emit = (
    newType: TransitionType,
    sid: string,
    msg: string,
    fid: string
  ) => {
    if (newType === "next_step") {
      onChange({ next_step_id: sid });
    } else if (newType === "end_flow") {
      onChange({ end_flow: true, message: msg });
    } else {
      onChange({ route_to_flow_id: fid });
    }
  };

  const handleTypeChange = (newType: TransitionType) => {
    setType(newType);
    emit(newType, nextStepId, endMessage, routeFlowId);
  };

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        {label}
      </Typography>
      <FormControl>
        <RadioGroup
          row
          value={type}
          onChange={(e) => handleTypeChange(e.target.value as TransitionType)}
        >
          <FormControlLabel value="next_step" control={<Radio size="small" />} label="Vai a step" />
          <FormControlLabel value="end_flow" control={<Radio size="small" />} label="Fine flow" />
          <FormControlLabel
            value="route_to_flow"
            control={<Radio size="small" />}
            label="Vai a altro flow"
          />
        </RadioGroup>
      </FormControl>

      {type === "next_step" && (
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Step destinazione</InputLabel>
          <Select
            label="Step destinazione"
            value={nextStepId}
            onChange={(e) => {
              setNextStepId(e.target.value);
              emit("next_step", e.target.value, endMessage, routeFlowId);
            }}
          >
            <MenuItem value="">— nessuno —</MenuItem>
            {steps.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                [{s.type}] {s.id.slice(0, 8)}… (ord. {s.order})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {type === "end_flow" && (
        <TextField
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          label="Messaggio di chiusura"
          value={endMessage}
          onChange={(e) => {
            setEndMessage(e.target.value);
            emit("end_flow", nextStepId, e.target.value, routeFlowId);
          }}
        />
      )}

      {type === "route_to_flow" && (
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>Flow destinazione</InputLabel>
          <Select
            label="Flow destinazione"
            value={routeFlowId}
            onChange={(e) => {
              setRouteFlowId(e.target.value);
              emit("route_to_flow", nextStepId, endMessage, e.target.value);
            }}
          >
            <MenuItem value="">— nessuno —</MenuItem>
            {flows.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};
